import { ComputedRef, ref, type Ref, unref, watch } from 'vue';
import { useLiveQueryV2, CODES } from '@libs/use';
import { waitFor } from '@libs/utils';
import type { MarketDepthItems, MarketDepthsQuery } from '@libs/api-reference';
import { AxiosPromise } from '@libs/http';

import { transformMarketDepthChange } from '../utils/transforms';
import { indexFactory, parentFactory } from '../utils/marketDepth';
import { SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX } from '../constants';

type UseLiveMarketDepthQueryFactoryType = {
  result: Ref<MarketDepthItems[]>;
  execute: ReturnType<typeof useLiveQueryV2>['execute'];
  isLoadingMarketDepth: Ref<boolean>;
  isRevalidatingMarketDepth: Ref<boolean>;
  isErrorMarketDepth: Ref<boolean>;
  marketDepthStatus: ReturnType<typeof useLiveQueryV2>['status'];
};

type useLiveMarketDepthQueryFactoryConfigs = {
  key: any[];
  isin: ComputedRef<string | null>;
  type: number;
  fetcher: (...arg: any[]) => AxiosPromise;
  sorter: (a: { price: number }, b: { price: number }) => number;
  immediate: boolean;
};

export default function useLiveMarketDepthQueryFactory({
  key,
  isin,
  type,
  sorter,
  fetcher,
  immediate,
}: useLiveMarketDepthQueryFactoryConfigs): UseLiveMarketDepthQueryFactoryType {
  const {
    result,
    execute,
    isPending: isLoadingMarketDepth,
    isRevalidating: isRevalidatingMarketDepth,
    isError: isErrorMarketDepth,
    status: marketDepthStatus,
  } = useLiveQueryV2<MarketDepthsQuery>({
    getChannels: () => [`${SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX}${unref(isin)}`],
    immediate,
    fetcher,
    key,
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolMarketDepthChangeSignal]: transformMarketDepthChange,
      };

      if (actions[signal.code] && result.value?.data?.items) {
        const data = actions[signal.code](signal.data);

        if (data.type !== type) {
          return;
        }
        const parentIndex = (() => {
          if (data.price) {
            return result.value.data.items.findIndex(
              (item) => item.price === data.price
            );
          }

          return result.value.data?.items?.findIndex((item) => {
            const childIndex = item.depthModels?.findIndex(
              (child) => child.uniqueKey === data.uniqueKey
            );

            return childIndex !== undefined && childIndex > -1;
          });
        })();

        const parent = result.value.data.items[parentIndex];

        const childIndex = parent?.depthModels?.findIndex(
          (item) => item.uniqueKey === data.uniqueKey
        );

        if (data.changeType === 'create') {
          if (parentIndex === -1) {
            result.value.data.items.push(
              parentFactory(data, indexFactory(data))
            );
          } else {
            parent.depthModels?.push(indexFactory(data));

            parent.volume =
              parent?.depthModels?.reduce(
                (sum, item) => sum + item.orderDisplayedQuantity,
                0
              ) ?? 0;
          }
        } else if (data.changeType === 'modify') {
          if (parentIndex === -1) {
            result.value.data.items.push(
              parentFactory(data, indexFactory(data))
            );
          } else if (childIndex !== undefined) {
            parent.depthModels?.splice(childIndex, 1, indexFactory(data));

            parent.volume =
              parent?.depthModels?.reduce(
                (sum, item) => sum + item.orderDisplayedQuantity,
                0
              ) ?? 0;
          }
        } else if (data.changeType === 'remove') {
          if (parentIndex > -1 && childIndex !== undefined) {
            parent.depthModels?.splice(childIndex, 1);

            parent.volume =
              parent?.depthModels?.reduce(
                (sum, item) => sum + item.orderDisplayedQuantity,
                0
              ) ?? 0;

            if (!parent.depthModels?.length) {
              result.value.data.items.splice(parentIndex, 1);
            }
          }
        } else if (data.changeType === 'remove-all') {
          result.value.data.items = [];

          waitFor(5000).then(execute);
        }
      }
    },
  });

  const marketDepth = ref<MarketDepthItems[]>([]);

  watch(
    () => result.value?.data.items,
    () => {
      marketDepth.value = result.value?.data.items?.sort(sorter) || [];
    },
    {
      deep: true,
    }
  );

  return {
    execute,
    result: marketDepth,
    isLoadingMarketDepth,
    isRevalidatingMarketDepth,
    isErrorMarketDepth,
    marketDepthStatus,
  };
}
